'use client';

import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './producthome.css';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, Typography } from '@mui/material';
import * as styles from './tableComponents/ProductPage_style';
import SubLevelInfo from './filterComponents/SubLevelInfo';
import FilterSection from './filterComponents/FilterSection';
import ProductFilterContainer from './filterComponents/ProductFilterContainer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    useGetFilterQuery,
    useLazyGetBasePartWithLimitNewQuery,
    useLazyGetFilterQuery,
} from '../../state/querySlice';
import ProductsBreadCrumbs from './tableComponents/ProductsBreadCrumbs';
import { convertToCSV, downloadCSV } from '../../utils/CSV';
import { getDisplayNameWithFallback, replacePercent20ToSpace } from '../../utils/StringRelated';
import VerticalContainer from '../common/VerticalContainer';
import { DetectedDefinition } from '../../schemas/DetectedDefinition';
import ToolPage from '../resourcesPage/simulationPage/ToolPage';
import { DynamicResponseBody } from '../../schemas/DynamicResponseBody';
import { DynamicRequestBody } from '../../schemas/DynamicRequestBody';
import { DynamicRequestBodyForBasePart } from '../../schemas/DynamicRequestBodyForBasePart';
import { ReturnParameterInFilter } from '../../schemas/ReturnParameterInFilter';
import { useLocale } from '../../contexts/LocaleProvider';
import { IntlProvider } from 'react-intl';
import { LanguageMapping } from '../../utils/LanguageMapping';
import FilterCheckBox from './filterComponents/FilterCheckBox';
import { ReturnParameterValueInFilter } from '../../schemas/ReturnParameterValueInFilter';
import { DisplayName } from '../../schemas/DisplayName';
import FilterButtonSection from './filterComponents/FilterButtonSection';
import ProductTable from './tableComponents/ProductTable';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import PreFilteredResourcesList from '../common/resourceLibrary/PreFilteredResourcesList';
import { useLoading } from '../../contexts/LoadingContext';
import { useSearchHandler } from '../../utils/useSearchHandler';
import { renderBoldText } from '../../utils/RenderBoldText';
import { useResourceTypes } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { ReturnParameterValue } from '../../schemas/ReturnParameterValue';
import { BasePartParameterValue } from '../../schemas/BasePartParameterValue';
import { useResponsive } from '../../contexts/ResponsiveContext';
import ReusableTabs from '../common/ReusableTabs';
import MobileProductDisplay from './filterComponents/MobileProductDisplay';
import MobileFilterSection from './filterComponents/MobileFilterSection';
import CompareContainer from './filterComponents/CompareContainer';
import { ProductGroupByDmDefinitionId } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { ProductGroup } from '../../graphql/pimcore/modules/resourceLibrary/types/ProductGroup';
import LargeScreenBreadcrumbsContainer from '../common/LargeScreenBreadcrumbsContainer';
import { useDefinitions } from '../../contexts/DefinitionsContext';
import { useCompareParams } from '../../utils/useCompareParams';
import { PIMCORE_CONTENT_BASE_URL } from '../../utils/Pimcore';
import { ProductsContent } from '../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { sanitizeKey } from '../../utils/Security';
import { useInitialSearchLoading } from './hooks/useInitialSearchLoading';
import { useSalesAndResourcesSimulationToolsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useSalesAndResourcesSimulationToolsContent';
import { ReturnParameterValues } from '../../schemas/ReturnParameterValues';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function getProductUrl(
    productField?: DetectedDefinition,
    productChildField?: DetectedDefinition
): string {
    if (!productField) return '/'; // ← 給固定值，避免 SSR "" / CSR "/"

    let url = `/products/${productField.displayName}`;

    if (productChildField) {
        url += `?79=${productChildField.id}`;
    }

    return url;
}

export function getProductFieldFromPath(pathname: string) {
    const segments = pathname.split('/').filter((segment) => segment.length > 0);
    const productIndex = segments.findIndex((segment) => segment.toLowerCase() === 'products');
    if (productIndex !== -1 && segments.length > productIndex + 1) {
        return replacePercent20ToSpace(segments[productIndex + 1] ?? '');
    }
    return '';
}

export function getProductChildFieldFromPath(pathname: string) {
    const segments = pathname.split('/').filter((segment) => segment.length > 0);
    const productIndex = segments.findIndex((segment) => segment.toLowerCase() === 'products');
    if (productIndex !== -1 && segments.length > productIndex + 2) {
        return replacePercent20ToSpace(segments[productIndex + 2] ?? '');
    }

    // ── 新增：處理 ?79=<only‑digits> 的情況 ───────────────────
    /** ──────────── 2. 新邏輯：只接受單一數字的 ?79=xxx ────────────
     * - `[?&]79=`   : 找到 79 這個參數（可能是 querystring 第一個 `?`，也可能是後面的 `&`）
     * - `(\d+)`     : 只捕捉「純數字」(至少 1 位)
     * - `(?:[&#]|$)`：其後必須是結束、`#`，或下一個 `&`，才能確定沒有其它字元
     *
     *   ➜ ?79=558           → ✔ 捕捉 "558"
     *   ➜ ?79=397%2C558     → ✘ 不符 (有 %2C)
     *   ➜ ?79=397,558       → ✘ 不符 (有 ,)
     *   ➜ ?79=558&foo=bar   → ✔ 捕捉 "558"
     */
    const m = pathname.match(/[?&]79=(\d+)(?:[&#]|$)/);
    if (m) {
        return m[1]; // 只有「一組純數字」才會進到這裡
    }

    return '';
}

type ProductsBannerProps = {
    children: React.ReactNode;
    /**
     * 由外層傳入的 PIM-Core 定義資料，可選
     * 若含有 backgroundImageUrl 就優先採用
     */
    backgroundImageUrl?: string;
};

function ProductsBanner({ children, backgroundImageUrl }: ProductsBannerProps) {
    const bannerImage = backgroundImageUrl
        ? `${PIMCORE_CONTENT_BASE_URL}${backgroundImageUrl}`
        : '';

    const productsBannerSx = {
        padding: '2.13rem 3.88rem 3rem',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(rgba(183, 83, 50, 0.8), rgba(183, 83, 50, 0.8)), url(${bannerImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover', // 新增此行來達到 object-fit: cover 效果
        width: '100%',
        color: 'white',
    };

    return <Box sx={productsBannerSx}>{children}</Box>;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `product-tab-${index}`,
        'aria-controls': `product-tabtabpanel-${index}`,
    };
}

interface ProductHomeProps {
    stateFromQueryRaw?: DynamicRequestBody;
    queryRawResults?: ReturnParameterValues;
    searchInput?: string | undefined;
    leftOverParam?: string | undefined;
    customBannerMessage?: string | undefined;
    shouldClearAllProps?: boolean;
    productGroupByDmDefinitionId: ProductGroupByDmDefinitionId;
    contentTree: ProductsContent;
}

export interface FilterParameterAndParameterValues {
    parameterDisplayNames: DisplayName[];
    parameterId: number;
    parameterName: string;
    parameterValues: ReturnParameterValueInFilter[];
}

export interface TwoMaps {
    single: Map<string, FilterParameterAndParameterValues>;
    multiple: Map<string, FilterParameterAndParameterValues>;
}

export const getParameterValuesFromRaw = (
    queryRawResults: { queryParameterValues: ReturnParameterValue[] } | undefined
): TwoMaps => {
    const single = new Map<string, FilterParameterAndParameterValues>();
    const multiple = new Map<string, FilterParameterAndParameterValues>();

    if (!queryRawResults) {
        return { single, multiple };
    }

    queryRawResults.queryParameterValues.forEach((pv) => {
        const key = pv.parameterId.toString();
        const paramValue = {
            displayNames: pv.displayNames,
            formattedValue: pv.formattedValue,
            id: pv.id,
            value: pv.value,
        };

        // 判斷 pv.parameterName 是否包含 "Features" 並且 value 為 "1"
        if (pv.parameterName.includes('Features') && pv.value === '1') {
            paramValue.value = 'true';
            paramValue.formattedValue = 'true';
        }

        if (multiple.has(key)) {
            // 如果已在 multiple，就直接 push 新值
            multiple.get(key)!.parameterValues.push(paramValue);
        } else if (single.has(key)) {
            // 如果已在 single，則移除後加入 multiple
            const existing = single.get(key)!;
            existing.parameterValues.push(paramValue);
            single.delete(key);
            multiple.set(key, existing);
        } else {
            // 新的 key，先放到 single
            single.set(key, {
                parameterDisplayNames: pv.parameterDisplayNames,
                parameterId: pv.parameterId,
                parameterName: pv.parameterName,
                parameterValues: [paramValue],
            });
        }
    });

    return { single, multiple };
};

export const mergeMissingParameters = (
    queryRawPV: Map<string, FilterParameterAndParameterValues>,
    filterPV: Map<string, FilterParameterAndParameterValues>
): Map<string, FilterParameterAndParameterValues> => {
    // 創建新的 Map，先複製 filterPV 的所有內容
    const resultMap = new Map(filterPV);

    // 檢查 queryRawPV 中的每個 key
    queryRawPV.forEach((value, key) => {
        // 如果 key 不存在於 resultMap 中，則加入
        if (!resultMap.has(key)) {
            resultMap.set(key, value);
        }
    });

    return resultMap;
};

const filterDynamicResponseForFilters = (
    data: DynamicResponseBody | undefined
): ReturnParameterInFilter[] | undefined => {
    if (!data) {
        return undefined;
    }

    const { '1': _ignore1, counts: _ignoreCounts, ...rest } = data;
    const filteredEntries = Object.entries(rest).filter(([_, value]) => {
        if (value && typeof value === 'object' && 'dataType' in value && 'parameterId' in value) {
            return value.dataType !== 'tinyint' && value.parameterId !== 2;
        }
        return true;
    });

    return filteredEntries.map(([, val]) => val as ReturnParameterInFilter);
};

// filterDynamicResponseForCheckBox 函數
export const filterDynamicResponseForCheckBox = (
    data: DynamicResponseBody | undefined
): ReturnParameterInFilter[] | undefined => {
    if (!data) {
        return undefined;
    }

    const { '1': _ignore1, counts: _ignoreCounts, ...rest } = data;
    const filteredEntries = Object.entries(rest).filter(([_, value]) => {
        return (
            value &&
            typeof value === 'object' &&
            'dataType' in value &&
            value.dataType === 'tinyint'
        );
    });

    return filteredEntries.map(([, val]) => val as ReturnParameterInFilter);
};

// filterDynamicResponseForMFG 函數
export const filterDynamicResponseForMFG = (
    data: DynamicResponseBody | undefined
): ReturnParameterInFilter | undefined => {
    if (!data) {
        return undefined;
    }

    const { '1': _ignore1, counts: _ignoreCounts, ...rest } = data;

    for (const [_, value] of Object.entries(rest)) {
        if (
            value &&
            typeof value === 'object' &&
            'parameterId' in value &&
            value.parameterId === 2
        ) {
            return value as ReturnParameterInFilter; // 找到後直接回傳
        }
    }
    // 如果沒有找到符合條件的值，回傳 undefined
    return undefined;
};

const cleanFilterState = (filterState: DynamicRequestBody): DynamicRequestBody => {
    return Object.entries(filterState)
        .filter(([key, value]) => {
            // 1. value 需存在且非空陣列
            if (value == null || (Array.isArray(value) && value.length === 0)) return false;

            // 2. key 僅保留 stock / features / 數字字串
            if (key === 'stock' || key === 'features') return true;
            return !Number.isNaN(Number(key));
        })
        .reduce<DynamicRequestBody>((result, [key, value]) => {
            const safeKey = sanitizeKey(key);
            if (!safeKey) return result;

            if (key === '1') {
                // 你知道它一定是 number；若有風險，這裡先做檢查再 as
                result[key] = value as number;
            } else {
                (result as Record<string, unknown>)[key] = value;
            }
            return result;
        }, {} as DynamicRequestBody);
};

// 建立空的 DisplayName 陣列
const emptyDisplayNames: DisplayName[] = [];

// 建立空的 ReturnParameterValueInFilter 陣列
const emptyParameterValues: ReturnParameterValueInFilter[] = [];

// 建立預設的 ReturnParameterInFilter
export const emptyMFGParameterFilter = (): ReturnParameterInFilter => ({
    dataType: 'bigint',
    parameterDisplayNames: emptyDisplayNames,
    parameterId: 2,
    parameterName: 'Manufacturer',
    parameterValues: emptyParameterValues,
    sortIndex: null,
    customColumn: false,
});

const checkMultipleSelectValues = (
    filterState: DynamicRequestBody,
    originData?: DynamicResponseBody
): Map<string, FilterParameterAndParameterValues> => {
    if (!originData) return new Map();

    const resultMap = new Map<string, FilterParameterAndParameterValues>();

    Object.entries(filterState)
        .filter(([_, value]) => Array.isArray(value) && value.length >= 2)
        .forEach(([key, value]) => {
            // value 為陣列，代表多選的值
            const selectedValues = value as Array<string | number | boolean>;
            const numericKey = Number(key);

            // 遍歷 originData 中所有參數資料（型別為 ReturnParameterInFilter）
            for (const item of Object.values(originData)) {
                const parameter = item as ReturnParameterInFilter;

                // 先判斷 key 是否能轉為數字，若能則比對 parameterId，否則以 parameterName 比對
                if (!isNaN(numericKey) && parameter.parameterId === numericKey) {
                    // 過濾出該 parameter 中被選取的值
                    const filteredValues = parameter.parameterValues.filter((pv) =>
                        selectedValues.includes(pv.id)
                    );
                    const mapKey = parameter.customColumn
                        ? parameter.parameterName
                        : parameter.parameterId.toString();
                    resultMap.set(mapKey, {
                        parameterDisplayNames: parameter.parameterDisplayNames,
                        parameterId: parameter.parameterId,
                        parameterName: parameter.parameterName,
                        parameterValues: filteredValues,
                    });
                    break;
                } else if (parameter.parameterName === key) {
                    const filteredValues = parameter.parameterValues.filter((pv) =>
                        selectedValues.includes(pv.id)
                    );
                    const mapKey = parameter.customColumn
                        ? parameter.parameterName
                        : parameter.parameterId.toString();
                    resultMap.set(mapKey, {
                        parameterDisplayNames: parameter.parameterDisplayNames,
                        parameterId: parameter.parameterId,
                        parameterName: parameter.parameterName,
                        parameterValues: filteredValues,
                    });
                    break;
                }
            }
        });

    return resultMap;
};

export const checkSingleSelectValues = (
    filterState: DynamicRequestBody,
    originData?: DynamicResponseBody
): Map<string, FilterParameterAndParameterValues> => {
    if (!originData) return new Map();

    const resultMap = new Map<string, FilterParameterAndParameterValues>();

    Object.entries(filterState)
        .filter(
            ([_, value]) =>
                // 只處理陣列長度為 1 或是布林值
                (Array.isArray(value) && value.length === 1) || typeof value === 'boolean'
        )
        .forEach(([key, value]) => {
            // 嘗試將 key 轉成數字，若可行就用 parameterId 的比對
            const numericKey = Number(key);
            // 如果 value 是陣列則取第一個值，若為布林值則直接使用
            const parameterValueId = Array.isArray(value) ? value[0] : value;

            // 遍歷所有 originData 中的參數（型別為 ReturnParameterInFilter）
            for (const item of Object.values(originData)) {
                const parameter = item as ReturnParameterInFilter;

                // 依據 parameterId 或 parameterName 來比對
                if (
                    (!isNaN(numericKey) && parameter.parameterId === numericKey) ||
                    parameter.parameterName === key
                ) {
                    let selectedParameterValue: ReturnParameterValueInFilter | undefined;

                    if (parameterValueId === true) {
                        // 當選擇值為布林 true 時，產生一個 dummy 的選項
                        selectedParameterValue = {
                            id: parameter.parameterValues[0]?.id ?? 0, // 可依需求調整 id 的來源
                            value: true,
                            formattedValue: true,
                            displayNames: null,
                        };
                    } else {
                        // 否則，從 parameterValues 中找出對應的選項
                        selectedParameterValue = parameter.parameterValues.find(
                            (pv) => pv.id === parameterValueId
                        );
                    }

                    if (selectedParameterValue) {
                        // 根據 customColumn 決定 key
                        const mapKey = parameter.customColumn
                            ? parameter.parameterName
                            : parameter.parameterId.toString();

                        if (!resultMap.has(mapKey)) {
                            resultMap.set(mapKey, {
                                parameterDisplayNames: parameter.parameterDisplayNames,
                                parameterId: parameter.parameterId,
                                parameterName: parameter.parameterName,
                                parameterValues: [selectedParameterValue],
                            });
                        } else {
                            resultMap.get(mapKey)!.parameterValues.push(selectedParameterValue);
                        }
                        // 找到匹配的 parameter 後即停止遍歷當前 filterState 的這筆項目
                        break;
                    }
                }
            }
        });

    return resultMap;
};

const getPartCount = (
    productField: DetectedDefinition | undefined,
    productChildField: DetectedDefinition | undefined,
    partCounts: number | undefined
) => {
    const defaultCounts = productChildField?.partCount || productField?.partCount;

    return partCounts ?? defaultCounts ?? 0;
};

const isFiltersContainsValue = (globalFilterState: Record<string, any>): boolean => {
    if (!globalFilterState) {
        return false;
    }
    return Object.entries(globalFilterState).some(([key, value]) => {
        // 跳過 key 為 "1" 以及 compare 與 doCompare 的情況
        if (key === '1' || key === 'compare' || key === 'doCompare') {
            return false;
        }
        // 檢查其他 key 的值是否存在
        return value !== undefined && value !== null;
    });
};

const isFilterOnlyContainsType = (globalFilterState: Record<string, any>): boolean => {
    if (!globalFilterState) {
        return false;
    }
    const filteredKeys = Object.keys(globalFilterState).filter((key) => key !== '1');
    return filteredKeys.length === 1 && filteredKeys[0] === '79';
};

const getFilterDataValues = (data: DynamicResponseBody | undefined) => {
    if (!data) return {};

    const result: { [key: string]: number } = {};

    Object.entries(data).forEach(([key, value]) => {
        if (key !== '1' && key !== 'counts' && key !== 'datasheets') {
            const param = value as ReturnParameterInFilter;
            result[param.parameterName] = param.parameterValues.length;
        }
    });

    return result;
};

function isSameOriginData(prevData?: DynamicResponseBody, nextData?: DynamicResponseBody): boolean {
    // 1. 兩個都是 undefined ⇒ 視為「沒變化」
    if (!prevData && !nextData) return true;

    // 2. 其中一個 undefined ⇒ 一定不同
    if (!prevData || !nextData) return false;

    // 3. 原笨的邏輯
    // 1) 比較 '1'
    if (prevData['1'] !== nextData['1']) return false;

    // 2) 比較 counts
    if (prevData.counts !== nextData.counts) return false;

    // 3) 比較 key 的數量
    const prevKeys = Object.keys(prevData);
    const nextKeys = Object.keys(nextData);
    if (prevKeys.length !== nextKeys.length) return false;

    // 4) 逐一檢查除了 '1'、counts 以外的 key
    //    如果對應的 value 是 ReturnParameterInFilter，就比較 parameterValues.length
    for (const key of prevKeys) {
        // 跳過 '1' 與 counts
        if (key === '1' || key === 'counts') continue;

        // 如果 nextData 裡沒有這個 key，直接不相同
        if (!(key in nextData)) return false;

        const prevVal = prevData[key];
        const nextVal = nextData[key];

        // 假設只有當 value 為 ReturnParameterInFilter 時，才需要比較 parameterValues 長度
        // 所以我們先檢查是否是物件、且存在 parameterValues 屬性
        if (isReturnParameterInFilter(prevVal) && isReturnParameterInFilter(nextVal)) {
            // 比較 parameterValues 的長度
            const prevParamValues = prevVal.parameterValues || [];
            const nextParamValues = nextVal.parameterValues || [];
            if (prevParamValues.length !== nextParamValues.length) {
                return false;
            }
        }
        // 其他型別（布林、字串、數字等）若不需要比較，則不做處理
        // 如果需要進一步比較，請自行在此區加上邏輯
    }

    return true;
}

/** 小工具：用來判斷某個值是否符合 ReturnParameterInFilter 的「結構」 */
function isReturnParameterInFilter(value: unknown): value is ReturnParameterInFilter {
    if (!value || typeof value !== 'object') return false;
    // 簡單判斷必要欄位 parameterValues 是否存在
    return 'parameterValues' in value;
}

function extractTypeParameterIds(response: DynamicResponseBody | undefined): number[] | undefined {
    if (!response) return undefined;

    // 嘗試從 response 中取得 key 為 "Type" 的參數資料
    const typeParam = response['Type'];
    // 若存在且有 parameterValues 陣列，就取出每個 parameterValues 的 id
    if (typeParam && typeof typeParam === 'object') {
        // 轉型為 ReturnParameterInFilter
        const param = typeParam as ReturnParameterInFilter;
        // 若存在 parameterValues 並且是陣列，就取出每個 parameterValues 的 id
        if (Array.isArray(param.parameterValues)) {
            return param.parameterValues.map((item) => item.id);
        }
    }
    // 若找不到則回傳 undefined
    return undefined;
}

export function useStableOriginData(
    originData: DynamicResponseBody | undefined,
    applyFilterResults?: DynamicResponseBody | undefined
): DynamicResponseBody | undefined {
    // 分別對 originData 與 applyFilterResults 進行 memoization
    const memoizedOriginData = useMemo(
        () => originData,
        [originData ? JSON.stringify(originData) : '']
    );
    const memoizedApplyFilterResults = useMemo(
        () => applyFilterResults,
        [applyFilterResults ? JSON.stringify(applyFilterResults) : '']
    );

    const prevDataRef = useRef<DynamicResponseBody | undefined>(
        memoizedApplyFilterResults || memoizedOriginData
    );

    return useMemo(() => {
        let result: DynamicResponseBody | undefined;
        // 如果 applyFilterResults 有值，直接回傳並更新 ref
        if (applyFilterResults) {
            prevDataRef.current = applyFilterResults;
            result = applyFilterResults;
        } else if (!originData) {
            // 如果 originData 不存在，清空 ref 並回傳 undefined
            prevDataRef.current = undefined;
            result = undefined;
        } else if (!prevDataRef.current) {
            // 初次設定
            prevDataRef.current = originData;
            result = originData;
        } else if (!isSameOriginData(prevDataRef.current, originData)) {
            // 比對是否與前一次不同
            prevDataRef.current = originData;
            result = originData;
        } else {
            // 如果相同，直接回傳先前的值
            result = prevDataRef.current;
        }
        return result;
    }, [originData, applyFilterResults]);
}

const DEFAULT_BANNER_TEXT =
    "YAGEO Group's extensive portfolio of capacitors covers 96% of all dielectric options available that find usage in automotive, industrial, telecommunications, defense, and consumer electronics.";

// New request for lv1 definition https://www.wrike.com/open.htm?id=1682086490
const chooseProperDefinition = (
    pimcoreTreeDefinitionData: ProductGroup | undefined,
    productChildField: DetectedDefinition | undefined
) => {
    return (
        pimcoreTreeDefinitionData?.children?.find(
            (lv1) =>
                lv1.dmDefinitionId === productChildField?.id.toString() &&
                lv1.longBlurb && // 避免 null / undefined / ''
                lv1.longBlurb.trim() // 避免空字串（含空白）) ?? pimcoreTreeDefinitionData;
        ) ?? pimcoreTreeDefinitionData
    );
};

/**
 * 根據 productHomeProps 的值，回傳要顯示的 banner 文字
 * 若文字中含有 **xx** 標記，就會用 <strong> 包起來
 */
function getBannerText(
    props: ProductHomeProps | undefined,
    pimcoreTreeDefinitionData: ProductGroup | undefined,
    productChildField: DetectedDefinition | undefined,
    language: string
): React.ReactNode {
    if (props?.searchInput) {
        // 有輸入搜尋字串才會檢查下方邏輯
        console.log('props', props);
        console.log('language', language);
        if (props.queryRawResults?.localizedMessages?.length &&
            language !== 'ENGLISH'
        ) {
            const translatedMessages = getDisplayNameWithFallback(
                props.queryRawResults?.localizedMessages,
                language,
                'ENGLISH'
            );
            console.log('translatedMessages', translatedMessages);
            return renderBoldText(translatedMessages);
        }

        if (
            props.queryRawResults?.detectedUniqueParts &&
            props.queryRawResults.detectedUniqueParts.length > 1
        ) {
            // 多於一個符合的零件，取出 message.text 顯示
            const text = props.queryRawResults.message?.text ?? '';
            // 用 renderBoldText 做預處理 => 把 *...* 替換成 <strong>
            return renderBoldText(text);
        }
        // 如果搜尋到的結果只有一個或沒有，這裡就回傳空字串
        return '';
    }

    const pimcoreDefinitionData = chooseProperDefinition(
        pimcoreTreeDefinitionData,
        productChildField
    );

    // 如果沒有任何搜尋字串，就顯示預設的字串
    return pimcoreDefinitionData?.longBlurb ?? DEFAULT_BANNER_TEXT;
}

/**
 * 把 URLSearchParams 轉成「跟篩選邏輯相關」的 fingerprint
 *  - 忽略 compare / doCompare / search (可依需求調整)
 *  - key 排序，value 亦 canonicalize（支援逗號分隔數字清單）
 */
function getFilterFingerprint(params: URLSearchParams): string {
    const IGNORE = new Set(['compare', 'doCompare']); // 要排除的 key

    return Array.from(params.entries())
        .filter(([k]) => !IGNORE.has(k))
        .map(([k, v]) => `${k}=${canonicalValue(v)}`)
        .sort((a, b) => a.localeCompare(b)) // key 排序（保證順序一致）
        .join('&');
}

/**
 * 若 value 是 "num,num,num" 形式，切開→數字排序→再黏回去；
 * 其他情況維持原字串
 */
function canonicalValue(v: string): string {
    // 簡單偵測「全數字+逗號」
    if (/^\d+(,\d+)+$/.test(v)) {
        return v
            .split(',')
            .map(Number)
            .sort((a, b) => a - b)
            .join(',');
    }
    return v;
}

const hasNonEmptyKey1 = (state: Record<string, unknown>): boolean => {
    const v = state['1'];
    if (v === undefined || v === null) return false;
    return String(v).trim() !== '';
};

const ProductHome: FunctionComponent<ProductHomeProps> = (ProductHomeProps) => {
    const navigate = useNavigate();

    // for locales
    const { locale, messages } = useLocale();
    const language = LanguageMapping.get(locale) || 'ENGLISH';
    const { contentTree: toolsContent } = useSalesAndResourcesSimulationToolsContent();

    const [searchParams, setSearchParams] = useSearchParams(undefined);

    const [value, setValue] = React.useState(0);

    const [isBasePartTableLoading, setIsBasePartTableLoading] = useState<boolean>(false);

    const [rowsPerPage, setRowsPerPage] = useState<number>(25);
    const [ascColumn, setAscColumn] = useState<string>('');
    const [descColumn, setDescColumn] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [isNumericSort, setIsNumericSort] = useState<boolean>(false);
    const { resourceTypeByKey } = useResourceTypes();
    const resourceTypeKeysWithoutDataSheet = Object.keys(resourceTypeByKey).filter(
        (key) => key !== 'Datasheet' && !key.includes('/')
    );

    const prevAscColumnRef = useRef(ascColumn);
    const prevDescColumnRef = useRef(descColumn);
    const prevPageRef = useRef(page);
    const prevRowsPerPageRef = useRef(rowsPerPage);

    const [partCounts, setPartCounts] = useState<number | undefined>(0);

    const [resetFilterSignal, setResetFilterSignal] = useState<number>(0);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const [storedSearchInput, setStoredSearchInput] = useState<ProductHomeProps | undefined>();

    const location = useLocation();

    // 定義一個 ref 保存上一次送出的 cleanFilterState
    const prevCleanFilterStateRef = useRef<DynamicRequestBody | null>(null);

    const prevCleanBasePartStateRef = useRef<DynamicRequestBodyForBasePart | null>(null);

    // RTK Query的isLoading一直沒辦法正確返回ture, 所以自己定義state
    const [isFilterLoading, setIsFilterLoading] = React.useState(false);

    const [productField, setProductField] = useState<DetectedDefinition>();
    const [productChildField, setProductChildField] = useState<DetectedDefinition>();

    const [globalFilterState, setGlobalFilterState] = useState<DynamicRequestBody>();
    const [globalFilterStateAfterApply, setGlobalFilterStateAfterApply] = useState<
        DynamicRequestBody | undefined
    >();

    const [globalFilterBaseState, setGlobalFilterBaseState] = useState<DynamicRequestBody>();

    const [filterTrigger, { data: filteredData }] = useLazyGetFilterQuery();
    const [basePartTrigger, { data: basePartData }] = useLazyGetBasePartWithLimitNewQuery();

    const [sortedParts, setSortedParts] = useState<BasePartParameterValue[] | undefined>();

    // const [isApplyFilter, setIsApplyFilter] = React.useState(false);

    // 旗標：是否由 chip 刪除觸發
    const skipSearchInputGuard = useRef(false);

    // 旗標：是否由 clickApplyFilter觸發
    const clickApplyFilterGuard = useRef(false);

    useEffect(() => {
        // 當 shouldClearAllProps 存在 (或為 true) 時，清除 searchedParts
        if (ProductHomeProps.shouldClearAllProps) {
            setStoredSearchInput(undefined);
            setSortedParts([]);
        } else if (ProductHomeProps.searchInput) {
            // console.log(ProductHomeProps);
            setStoredSearchInput(ProductHomeProps);
        }
    }, [ProductHomeProps]);

    useEffect(() => {
        // console.log('[sortedParts‑effect] fired');
        // console.log('basePartData', basePartData);
        // console.log('basePartData', globalFilterStateAfterApply);

        if (!globalFilterStateAfterApply || !isFiltersContainsValue(globalFilterStateAfterApply)) {
            setSortedParts([]);
            return;
        }

        const orderMap: Record<string, number> = {};
        if (storedSearchInput?.queryRawResults?.detectedUniqueParts) {
            storedSearchInput?.queryRawResults?.detectedUniqueParts.forEach((value, index) => {
                orderMap[value.displayPn ?? 0] = index;
            });
            let bpCopy = [...(basePartData ?? [])];
            bpCopy?.sort((a, b) => {
                const aIndex = orderMap[a.basePn];
                const bIndex = orderMap[b.basePn];
                if (aIndex === undefined && bIndex === undefined) {
                    return 0;
                }
                if (aIndex === undefined) {
                    return 1;
                }
                if (bIndex === undefined) {
                    return -1;
                }
                return aIndex - bIndex;
            });
            setSortedParts(bpCopy);
        } else {
            setSortedParts(basePartData);
        }
    }, [
        basePartData,
        storedSearchInput?.queryRawResults?.detectedUniqueParts,
        globalFilterStateAfterApply,
    ]);

    const [appliedKeys, setAppliedKeys] = useState<Set<string>>(new Set());
    // 用不可變方式更新 appliedKeys
    const addKeyForAppliedKeys = useCallback((key: string) => {
        setAppliedKeys((prev) => {
            if (prev.has(key)) return prev; // 若已存在，直接返回舊引用
            const newSet = new Set(prev);
            newSet.add(key);
            return newSet;
        });
    }, []);

    const removeKeyForAppliedKeys = useCallback((key: string) => {
        setAppliedKeys((prev) => {
            if (!prev.has(key)) return prev;
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
        });
        setSingleSelectKey((prev) => {
            if (!prev.has(key)) return prev;
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
        });

        setMultipleSelectKeys((prev) => {
            if (!prev.has(key)) return prev;
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
        });
        setGlobalFilterState((prev) => {
            if (!prev) return prev;
            const newState = { ...prev };
            delete newState[key]; // 刪除該 key
            return newState;
        });

        // 更新 storedSearchInput 的 queryRawResults
        setStoredSearchInput((prev) => {
            if (!prev || !prev.queryRawResults) return prev;
            const newQueryRawResults = { ...prev.queryRawResults };
            newQueryRawResults.queryParameterValues =
                newQueryRawResults.queryParameterValues.filter(
                    (param) => param.parameterId.toString() !== key
                );
            return { ...prev, queryRawResults: newQueryRawResults };
        });
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const IGNORE = new Set(['search', 'compare', 'doCompare']);

        const urlKeys = new Set<string>();
        for (const [k] of params) if (!IGNORE.has(k)) urlKeys.add(k);

        setAppliedKeys((prev) => {
            const next = new Set([...prev].filter((key) => urlKeys.has(key)));

            // 判斷內容是否完全一樣
            const isSame = next.size === prev.size && [...next].every((key) => prev.has(key));

            return isSame ? prev : next;
        });
    }, [location.search]);

    // applyFilterResults 是按下 Apply Filter後, 要取代originData的filter資料
    const [applyFilterResults, setApplyFilterResults] = useState<DynamicResponseBody | undefined>();

    // filterResults是一般再點選時, 隨時更新的的filter資料
    const [filterResults, setFilterResults] = useState<DynamicResponseBody | undefined>();

    // 這是實際上query/raw的query-parameters送到request的樣子
    const prevQueryRawRef = useRef<DynamicRequestBody | undefined>(undefined);

    const prevStableOriginDataRef = useRef<DynamicResponseBody | undefined>(undefined);

    const { definitionsMap } = useDefinitions();

    useEffect(() => {
        setGlobalFilterStateAfterApply(undefined);
        setApplyFilterResults(undefined);
        setFilterResults(undefined);

        // 取得 productFieldString 跟productChildFieldString（使用 helper 函數）
        const productFieldString = getProductFieldFromPath(location.pathname);
        // console.log(productFieldString)

        const productChildFieldString = getProductChildFieldFromPath(location.search);
        setProductField(definitionsMap.get(productFieldString)?.get(productFieldString));

        // 若存在 productChildFieldString，就設定 productChildField
        if (productChildFieldString) {
            const productChildFieldId = parseInt(productChildFieldString, 10);

            if (!Number.isNaN(productChildFieldId)) {
                // 👉 參數是「純數字」→ 依 ID 取 definition
                setProductChildField(
                    Array.from(definitionsMap.get(productFieldString)?.values() ?? []).find(
                        (def) => def.id === productChildFieldId
                    )
                );
            } else {
                // 👉 不是數字 → 維持原本以 displayName 取 definition 的作法
                setProductChildField(
                    definitionsMap.get(productFieldString)?.get(productChildFieldString)
                );
            }
        } else {
            setProductChildField(undefined);
        }

        setIsFilterControllerOpen(true);
    }, [definitionsMap, location.pathname, location.search]);

    useEffect(() => {
        // 在 productField, 和 definitionsMap 都有效时才执行
        if (!productField || definitionsMap.size === 0) {
            return;
        }

        // 取得目前的 storedSearchInput.stateFromQueryRaw，若沒帶就用 undefined
        const newQueryRaw = storedSearchInput?.stateFromQueryRaw ?? undefined;

        // 用「JSON 字串」或「其它方式」比較前後是否不同
        const prevString = JSON.stringify(prevQueryRawRef.current ?? null);
        const newString = JSON.stringify(newQueryRaw ?? null);
        const isDifferent = prevString !== newString;

        if (newQueryRaw) {
            // 如果有來自搜尋的初始 filter state，優先使用它
            if (isDifferent) {
                // 先算出 key '1' 應該放的 id
                const treeId =
                    definitionsMap.get(productField.displayName)?.get(productField.displayName)
                        ?.id ?? 0;

                /* --------- 關鍵：把 key '1' 合併進 newQueryRaw --------- */
                // 若 newQueryRaw 已含 '1' 且你想「覆蓋」它 → 把 '1' 放在展開運算子 *前面*
                // 若想保留 newQueryRaw 原本的 '1'（若有） → 把 '1' 放在展開運算子 *後面*
                const mergedQueryRaw: DynamicRequestBody = { ...newQueryRaw, '1': treeId };
                // --------------------------------------------------------

                // 有帶條件 => 用它初始化
                setGlobalFilterState(JSON.parse(JSON.stringify(mergedQueryRaw)));
                prevQueryRawRef.current = JSON.parse(JSON.stringify(mergedQueryRaw));
                // 這邊還是設定baseState是最基本的樣子
                setGlobalFilterBaseState({ '1': treeId });

                // console.debug('newQueryRaw :', JSON.parse(JSON.stringify(newQueryRaw)));

                // 如果一開始有type就加入
                if (
                    newQueryRaw.hasOwnProperty('79') &&
                    Array.isArray(newQueryRaw['79']) &&
                    newQueryRaw['79'].length > 0
                ) {
                    addKeyForAppliedKeys('79');
                    if (newQueryRaw['79'].length === 1) {
                        // 假設 newQueryRaw['79'] 是數字陣列，取出唯一的 id
                        const targetId = newQueryRaw['79'][0];

                        // 取得對應 productField 的子定義 Map
                        const childDefsMap = definitionsMap.get(productField.displayName);

                        let foundDefinition: DetectedDefinition | undefined;
                        if (childDefsMap) {
                            // 遍歷 Map 中的所有 DetectedDefinition
                            for (const def of Array.from(childDefsMap.values())) {
                                if (def.id === targetId) {
                                    foundDefinition = def;
                                    break;
                                }
                            }
                        }

                        // 如果找到，就更新 productChildField
                        if (foundDefinition) {
                            // console.log("918 setProductChildField(foundDefinition) =",foundDefinition )
                            setProductChildField(foundDefinition);
                        } else {
                            console.error(`Can't find id: ${targetId} definition`);
                        }
                    }
                }

                // If we can find parts directly
                if (!shouldUseDetectedUniqueParts) {
                    // console.log("我在這裡handleApplyFilters")
                    handleApplyFilters(JSON.parse(JSON.stringify(newQueryRaw)));
                    return;
                }

                // If we needs to use DetectedUniqueParts
                const newSearchParams = new URLSearchParams(searchParams);
                // console.log('newSearchParams', newSearchParams.toString());

                updateSearchParams(JSON.parse(JSON.stringify(newQueryRaw)), newSearchParams);
                // console.log('updateSearchParamInNewQueryRaw', newSearchParams.toString());
                setSearchParams(newSearchParams);
            }
        }
    }, [productField, productChildField, definitionsMap, storedSearchInput]);

    const [firstKeyAllowedMultipleSelect, setFirstKeyAllowedMultipleSelect] = useState<
        string | null
    >(null);

    const [filterKeyOrder, setFilterKeyOrder] = useState<string[]>([]);
    useEffect(() => {
        if (globalFilterState) {
            // 比較 json的globalFilterState跟 globalFilterStateAfterApply 是否相等,如果相等就不做任何動作,如果不相等幫我直接進入到else區塊
            if (
                globalFilterStateAfterApply &&
                JSON.stringify(globalFilterState) === JSON.stringify(globalFilterStateAfterApply)
            ) {
                // console.debug(
                //     'globalFilterState',
                //     globalFilterState,
                //     'globalFilterStateAfterApply',
                //     globalFilterStateAfterApply
                // );
                return;
            } else {
                // 取得當前所有有效鍵（排除 '1' 且值不為空）
                let keys = Object.keys(globalFilterState).filter((key) => {
                    const val = globalFilterState[key] as any; // 斷言為 any
                    return key !== '1' && val != null && !(Array.isArray(val) && val.length === 0);
                });
                // 如果 globalFilterStateAfterApply 存在，先把它裡面的 key 移除
                if (globalFilterStateAfterApply) {
                    const appliedKeysFromGlobalFilterStateAfterApply = Object.keys(
                        globalFilterStateAfterApply
                    );
                    keys = keys.filter(
                        (key) => !appliedKeysFromGlobalFilterStateAfterApply.includes(key)
                    );
                }
                setFilterKeyOrder((prevOrder) => {
                    // 過濾掉原本已存在但現不在 keys 裡的鍵
                    const newOrder = prevOrder.filter((key) => keys.includes(key));
                    // 對於 keys 中有但 newOrder 沒有的鍵，依照 keys 的順序推到 newOrder 裡
                    keys.forEach((key) => {
                        if (!newOrder.includes(key)) {
                            newOrder.push(key);
                        }
                    });
                    return newOrder;
                });
            }
        } else {
            setFilterKeyOrder([]);
        }
    }, [globalFilterState, globalFilterStateAfterApply]);

    useEffect(() => {
        if (filterKeyOrder.length > 0) {
            setFirstKeyAllowedMultipleSelect(filterKeyOrder[0] ?? null);
        } else {
            setFirstKeyAllowedMultipleSelect(null);
        }
    }, [filterKeyOrder]);

    // originData是最一開始的filter data
    const { data: originData, isLoading: originDataLoading } = useGetFilterQuery(
        {
            '1':
                definitionsMap
                    .get(productField?.displayName ?? '')
                    ?.get(productField?.displayName ?? '')?.id ?? 0,
        },
        {
            skip:
                !definitionsMap
                    .get(productField?.displayName ?? '')
                    ?.get(productField?.displayName ?? '')?.id ||
                !productField ||
                definitionsMap.size < 1,
        }
    );

    const stableOriginData = useStableOriginData(originData, applyFilterResults);

    const [isApplyFiltersEnabled, setIsApplyFiltersEnabled] = useState<boolean>(false);

    const [multipleSelectKeys, setMultipleSelectKeys] = useState<
        Map<string, FilterParameterAndParameterValues>
    >(new Map());
    const [singleSelectKey, setSingleSelectKey] = useState<
        Map<string, FilterParameterAndParameterValues>
    >(new Map());

    // 將 globalFilterState 裡面的 key/value，排除 '1' 之後放進 searchParams
    const updateSearchParams = (
        globalFilterState: DynamicRequestBody,
        newSearchParams: URLSearchParams
    ) => {
        Object.entries(globalFilterState).forEach(([key, val]) => {
            const safeKey = sanitizeKey(key);
            if (!safeKey) return;
            if (key === '1' || val == null) {
                return; // 跳過
            }
            // ---------- 保留 search 原樣 ----------
            if (key === 'search' && typeof val === 'string') {
                newSearchParams.set('search', val.trim()); // URLSearchParams 會自動做 encode
                return;
            }
            // ---------- 其餘維持原本邏輯 ----------
            // 若是陣列，則用逗號把它串起來
            if (Array.isArray(val)) {
                const joined = val.join(',');
                if (joined.trim() === '') {
                    newSearchParams.delete(key);
                } else {
                    newSearchParams.set(key, joined);
                }
            } else {
                const strVal = String(val).trim();
                if (strVal === '') {
                    newSearchParams.delete(key);
                } else {
                    newSearchParams.set(key, strVal);
                }
            }
        });
    };

    // 我用這個 useEffect 更新 globalFilterState 並且執行 filterTrigger
    useEffect(() => {
        // console.group('%c🟡 filter‑useEffect RUN', 'color:orange;font-weight:bold');
        // console.log('❯ deps  globalFilterState:', globalFilterState);
        // console.log('❯ deps  storedSearchInput:', storedSearchInput?.stateFromQueryRaw);
        // console.log('❯ deps  stableOriginData :', stableOriginData);

        if (!globalFilterState || !stableOriginData) {
            // console.log('⏭ 沒有 globalFilterState 或 stableOriginData，直接 return');
            console.groupEnd();
            return;
        }

        // let newCleanState: DynamicRequestBody = cleanFilterState(globalFilterState);
        // // console.log('🔹 cleanFilterState →', newCleanState);
        //
        // // 如果 key '1' 為空，就補進目前 productField 的 treeId
        // if (!hasNonEmptyKey1(newCleanState)) {
        //     const treeId =
        //         definitionsMap
        //             .get(productField?.displayName ?? '')
        //             ?.get(productField?.displayName ?? '')?.id ?? 0;
        //     newCleanState = { ...newCleanState, '1': treeId };
        //     console.warn(`🔹 key "1" 缺失，補 treeId=${treeId} →`, newCleanState);
        // }

        // ① 先確保 globalFilterState 一定含 '1'
        const treeId =
            definitionsMap
                .get(productField?.displayName ?? '')
                ?.get(productField?.displayName ?? '')?.id ?? 0;

        const ensuredState: DynamicRequestBody = hasNonEmptyKey1(globalFilterState)
            ? globalFilterState // 已經有 '1'
            : { ...globalFilterState, '1': treeId }; // 沒有就補

        // ② 再丟進 cleanFilterState
        const newCleanState: DynamicRequestBody = cleanFilterState(ensuredState);

        /** -------------------------------------------------
         *  ① 先檢查「乾淨 filter 狀態」是否真的有變
         * ------------------------------------------------*/
        if (
            prevCleanFilterStateRef.current &&
            JSON.stringify(prevCleanFilterStateRef.current) === JSON.stringify(newCleanState)
        ) {
            // console.log('⏭ cleanFilterState 沒變——直接跳過，不送 request');
            // console.groupEnd();
            return;
        }

        /** -------------------------------------------------
         *  ② 更新兩個 ref（非常重要，避免下一輪重送）
         * ------------------------------------------------*/
        prevCleanFilterStateRef.current = newCleanState;
        prevStableOriginDataRef.current = stableOriginData;

        // console.log('✅ 要送 filterTrigger，payload =', newCleanState);

        /** -------------------------------------------------
         *  ③ 前端狀態計算
         * ------------------------------------------------*/
        const singlePVSelections = checkSingleSelectValues(globalFilterState, stableOriginData);
        const { single: singlePVFromRaw, multiple: multiplePVFromRaw } = getParameterValuesFromRaw(
            storedSearchInput?.queryRawResults
        );
        const mergeSinglePV = mergeMissingParameters(singlePVFromRaw, singlePVSelections);

        const multipleKeys = checkMultipleSelectValues(globalFilterState, stableOriginData);
        const mergeMultiplePV = mergeMissingParameters(multiplePVFromRaw, multipleKeys);

        setSingleSelectKey(mergeSinglePV);
        setMultipleSelectKeys(mergeMultiplePV);

        /** -------------------------------------------------
         *  ④ 送 API
         * ------------------------------------------------*/
        setIsFilterLoading(true);
        filterTrigger(newCleanState);
        // .then(() => console.log('✅ filterTrigger resolved'))
        // .catch((e) => console.error('❌ filterTrigger error', e));

        const newSearchParams = new URLSearchParams(window.location.search);

        /** -------------------------------------------------
         *  ⑤ 後續 UI 狀態
         * ------------------------------------------------*/
        if (isFiltersContainsValue(newCleanState)) {
            // console.log("我在 setIsApplyFiltersEnabled(true);", newCleanState)
            setIsApplyFiltersEnabled(true);
        } else if (newSearchParams.has('search')) {
            // console.log("url contains search")
            return;
        } else {
            if (!mergeSinglePV.size && !mergeMultiplePV.size) {
                // console.log('🔄 完全清空狀態，重置 storedSearchInput / sortedParts');
                setStoredSearchInput(undefined);
                setSortedParts([]);
            }
            setIsApplyFiltersEnabled(false);
            setAppliedKeys(new Set());
            setApplyFilterResults(undefined);
            setFilterResults(undefined);
            setFirstKeyAllowedMultipleSelect(null);
            setGlobalFilterStateAfterApply(undefined);
            setPartCounts(undefined);
            // console.log('我fallback');
            navigate(`/products/${productField?.displayName}`);
        }

        // console.groupEnd();
    }, [globalFilterState, storedSearchInput, stableOriginData]);

    const searchInputRef = useRef<string | null>(null);

    // 我用這個useEffect把更新FilterResult
    useEffect(() => {
        if (storedSearchInput?.searchInput) {
        }
        if (filteredData) {
            // console.log(
            //     'storedSearchInput = ',
            //     storedSearchInput,
            //     'filteredData =',
            //     filteredData,
            //     'clickApplyFilterGuard.current',
            //     clickApplyFilterGuard.current
            // );

            const prevFilterData = getFilterDataValues(filterResults);
            const currentFilterData = getFilterDataValues(filteredData);
            // 比較是否有變化
            const hasChanged = JSON.stringify(prevFilterData) !== JSON.stringify(currentFilterData);

            if (!storedSearchInput && clickApplyFilterGuard.current) {
                // console.log(
                //     '!storedSearchInput && clickApplyFilterGuard.current, setApplyFilterResult',
                //     filteredData
                // );
                setApplyFilterResults(filteredData);
            }

            // else if (!storedSearchInput && isApplyFilter) {
            //     console.log("!storedSearchInput && isApplyFilter, setApplyFilterResult", filteredData)
            //
            //     setApplyFilterResults(filteredData);
            //     setIsApplyFilter(false);
            // }

            if (hasChanged) {
                // console.log("hasChanged", filteredData)
                setFilterResults(filteredData);
                //click option後自動更新ApplyFilterResults的重點在這裡
                if (storedSearchInput) {
                    const fromChip = skipSearchInputGuard.current; // ← 讀旗標
                    const fromApplyFilterButton = clickApplyFilterGuard.current;

                    if (fromChip) {
                        // console.log('IM FROMCHIP', filteredData);
                        setApplyFilterResults(filteredData);
                    } else if (fromApplyFilterButton) {
                        // console.log('IM fromApplyFilterButton', filteredData);
                        setApplyFilterResults(filteredData);
                        if (storedSearchInput.searchInput) {
                            searchInputRef.current = storedSearchInput.searchInput;
                        }
                    } else if (storedSearchInput.searchInput) {
                        // console.log(
                        //     'storedSearchInput.searchInput',
                        //     storedSearchInput.searchInput,
                        //     'searchInputRef.current',
                        //     searchInputRef.current
                        // );
                        if (
                            searchInputRef.current === null ||
                            searchInputRef.current !== storedSearchInput.searchInput
                        ) {
                            searchInputRef.current = storedSearchInput.searchInput; // 記住新的關鍵字
                            // console.log('IM from searchInput', filteredData);
                            setApplyFilterResults(filteredData); // 觸發更新
                        }
                    }
                } else if (clickApplyFilterGuard.current) {
                    console.debug('normal click ApplyFilterButton, setApplyFilterResults');
                    setApplyFilterResults(filteredData);
                }
                setIsFilterLoading(false);
                if (
                    globalFilterState &&
                    (isFiltersContainsValue(cleanFilterState(globalFilterState)) ||
                        isFilterOnlyContainsType(cleanFilterState(globalFilterState)))
                ) {
                    setPartCounts(filteredData.counts);
                }
                if (
                    productChildField &&
                    globalFilterState &&
                    isFilterOnlyContainsType(cleanFilterState(globalFilterState))
                ) {
                    setApplyFilterResults(filteredData);
                    addKeyForAppliedKeys('79');
                }
            }
        }
        if (skipSearchInputGuard.current) {
            skipSearchInputGuard.current = false;
        }
        if (clickApplyFilterGuard.current) {
            clickApplyFilterGuard.current = false;
        }
    }, [filteredData, filterResults, productChildField, globalFilterState, storedSearchInput]);

    // 這邊是控制換頁
    useEffect(() => {
        if (globalFilterState) {
            const ascColumnChanged = prevAscColumnRef.current !== ascColumn;
            const descColumnChanged = prevDescColumnRef.current !== descColumn;
            const pageChanged = prevPageRef.current !== page;
            const rowsPerPageChanged = prevRowsPerPageRef.current !== rowsPerPage;

            if (ascColumnChanged || descColumnChanged || pageChanged || rowsPerPageChanged) {
                const body: DynamicRequestBodyForBasePart = {
                    ...globalFilterState,
                    asc: ascColumn || '',
                    desc: descColumn || '',
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                    isNumeric: isNumericSort,
                };

                // 比對前一次的 basePartTrigger 請求狀態
                if (
                    prevCleanBasePartStateRef.current &&
                    JSON.stringify(prevCleanBasePartStateRef.current) === JSON.stringify(body)
                ) {
                    console.debug('Base part state unchanged, skipping trigger');
                    return; // 相同則不發送新的請求
                }

                setIsBasePartTableLoading(true);
                (async () => {
                    try {
                        await basePartTrigger(body, false);
                    } catch (error) {
                        console.error('Failed to fetch data:', error);
                    } finally {
                        // 更新所有 ref 值
                        prevAscColumnRef.current = ascColumn;
                        prevDescColumnRef.current = descColumn;
                        prevPageRef.current = page;
                        prevRowsPerPageRef.current = rowsPerPage;
                        setIsBasePartTableLoading(false);
                    }
                })();
            }
        }
    }, [globalFilterState, ascColumn, descColumn, page, rowsPerPage]);

    // 這是當我按下ApplyFilter按鈕後
    const handleApplyFilters = async (newGlobalFilterState?: DynamicRequestBody | undefined) => {
        console.log('handleApplyFilters被呼叫了');

        // 如果有傳入新的 state，就以它作為當前值，否則使用全域的 globalFilterState
        const currentState = newGlobalFilterState ?? globalFilterState;
        if (!currentState) {
            console.log('!currentState return');
            return;
        } else {
            if (!isFiltersContainsValue(cleanFilterState(currentState))) {
                console.log('!isFiltersContainsValue(cleanFilterState(currentState)) return');
                return;
            }
        }

        if (currentState) {
            // 提取有值的鍵，排除 'targetDefinitionId' 跟 tree
            const appliedKeys = Object.keys(currentState).filter(
                (key) =>
                    key !== '1' && currentState[key] !== undefined && currentState[key] !== null
            );
            // console.log("setGlobalFilterStateAfterApply(currentState)", currentState)
            // console.log(currentState);
            setGlobalFilterStateAfterApply(currentState);
            // 不要刪
            appliedKeys.forEach((key) => addKeyForAppliedKeys(key));
            setFilterKeyOrder([]);

            const body: DynamicRequestBodyForBasePart = {
                ...currentState,
                asc: '',
                desc: '',
                limit: rowsPerPage,
                isNumeric: false,
            };
            // 比對前一次的 basePartTrigger 請求狀態
            if (
                prevCleanBasePartStateRef.current &&
                JSON.stringify(prevCleanBasePartStateRef.current) === JSON.stringify(body)
            ) {
                console.log('Base part state unchanged, skipping trigger');
                return; // 相同則不發送新的請求
            }
            setIsBasePartTableLoading(true);

            try {
                const result = await basePartTrigger(body, false).unwrap(); // 會拋出錯誤才進 catch
                // ✅ 這邊確定成功回傳，才執行
                clickApplyFilterGuard.current = true;
                // ====== 這裡開始處理更新 URL 的邏輯 ======
                // 建立新的 query 參數物件

                const newSearchParams = new URLSearchParams(searchParams);
                updateSearchParams(currentState, newSearchParams);

                if (newSearchParams.toString() !== searchParams.toString()) {
                    // console.log('🚀 push URL →', newSearchParams.toString());
                    setSearchParams(newSearchParams);
                } else {
                    // console.log('⏭  URL unchanged – skip push');
                }

                // ====== 以上為更新 URL 的邏輯 ======
            } catch (error) {
                console.error('Error triggering base part:', error);
            } finally {
                setIsBasePartTableLoading(false);
                setPage(0);
                if (useMobileProductDisplay) {
                    setIsFilterControllerOpen(false);
                }
            }
        }
    };

    // This function is used to remove chip on the top
    const onlyHandleFilterChange = (parameter?: string) => {
        if (globalFilterState) {
            // 提取有值的鍵，排除 'targetDefinitionId' 跟 tree
            if (parameter) {
                removeKeyForAppliedKeys(parameter);

                // 如果是type就拿掉ProductChildField
                if (parameter === '79') {
                    setProductChildField(undefined);
                }

                // 如果 globalFilterStateAfterApply 還不存在(尚未按下任何 Apply)，
                // 或者 parameter 不在 globalFilterStateAfterApply 內，
                // 就代表這個 parameter 根本不是從「已經 apply 過的條件」來的，
                // 不需要 setIsApplyingFilter(true)。

                // 用目前的 searchParams 建一個全新的實例
                const newSearchParams = new URLSearchParams(searchParams);
                updateSearchParams(globalFilterState, newSearchParams);
                // 在新實例裡刪除該 key
                newSearchParams.delete(parameter);
                newSearchParams.delete('search');

                skipSearchInputGuard.current = true;

                // https://www.wrike.com/open.htm?id=1653033605
                setStoredSearchInput((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        queryRawResults: prev.queryRawResults
                            ? {
                                  ...prev.queryRawResults,
                                  detectedUniqueParts: [],
                              }
                            : undefined,
                        searchInput: undefined,
                    };
                });

                // 最後再 setSearchParams(newSearchParams) 才會觸發 URL 更新
                // if (newSearchParams.size) {
                setSearchParams(newSearchParams);
                // }
                if (!globalFilterStateAfterApply || !(parameter in globalFilterStateAfterApply)) {
                    return;
                }
            }
        }
    };

    // 已經有globalFilterStateAfterApply 但是所有選項都被清空的話
    useEffect(() => {
        if (globalFilterStateAfterApply) {
            // 檢查是否只剩下 "1" 這個欄位
            const hasOnlyBaseField = Object.keys(globalFilterStateAfterApply).every(
                (key) => key === '1'
            );
            if (hasOnlyBaseField) {
                // setStoredSearchInput(undefined);
                setIsApplyFiltersEnabled(false);
                setAppliedKeys(new Set());
                setApplyFilterResults(undefined);
                setFilterResults(undefined);
                setFirstKeyAllowedMultipleSelect(null);
                setGlobalFilterStateAfterApply(undefined);
                setProductChildField(undefined);
                setSearchParams('');
                setGlobalFilterState((prevState) => {
                    if (!prevState) {
                        return prevState;
                    }
                    return {
                        '1': prevState['1'], // 保留 key 為 "1" 的值
                    };
                });
            }
        }
    }, [globalFilterStateAfterApply]);

    /**
     *  這個 useEffect 用來在 globalFilterStateAfterApply 更新時，
     *  檢查是否有 key "79" (Parameter ID = 79)
     *  如果沒有,就檢查 applyFilterResults 有沒有符合的
     *  最後用此值來 setProductChildField
     */
    useEffect(() => {
        if (globalFilterStateAfterApply) {
            // console.log('globalFilterStateAfterApply', productChildField);
            if (Object.prototype.hasOwnProperty.call(globalFilterStateAfterApply, '79')) {
                // 取得該值並更新子階層
                const childDefId = globalFilterStateAfterApply['79'] as number[];

                // 只處理 length === 1 的情況
                if (Array.isArray(childDefId) && childDefId.length === 1) {
                    const targetId = childDefId[0];

                    // 在 definitionsMap 中尋找符合 targetId 的 definition
                    let foundDefinition: DetectedDefinition | undefined;

                    definitionsMap.forEach((subMap) => {
                        subMap.forEach((def) => {
                            if (def.id === targetId) {
                                foundDefinition = def;
                            }
                        });
                    });
                    if (foundDefinition) {
                        // console.log("1509 setProductChildField(foundDefinition) =",foundDefinition )

                        setProductChildField(foundDefinition);
                        clickApplyFilterGuard.current = true;
                        return;
                    } else {
                        console.error(`Cant find match id:${targetId} in definitionsMap`);
                    }
                }
            }
        }
        // 若 globalFilterStateAfterApply 沒有符合的，則檢查 applyFilterResults
        if (applyFilterResults) {
            const param79 = Object.values(applyFilterResults).find((item) => {
                // 找 parameterId === 79
                return (
                    typeof item === 'object' && (item as ReturnParameterInFilter).parameterId === 79
                );
            }) as ReturnParameterInFilter | undefined;

            if (param79) {
                const values = param79.parameterValues;
                // 僅處理長度為 1 的情況
                if (values.length === 1) {
                    const onlyValue = values[0];
                    let displayNameToUse = '';

                    // 先從 displayNames 中找對應的 language
                    if (onlyValue?.displayNames && onlyValue.displayNames.length > 0) {
                        const matched = onlyValue.displayNames.find(
                            (d) => d.language === 'ENGLISH'
                        );
                        if (matched) {
                            displayNameToUse = matched.display;
                        }
                    }
                    if (globalFilterStateAfterApply) {
                        if (
                            !isFiltersContainsValue(cleanFilterState(globalFilterStateAfterApply))
                        ) {
                            setProductChildField(undefined);
                        } else {
                            setProductChildField(
                                definitionsMap
                                    .get(productField?.displayName ?? '')
                                    ?.get(displayNameToUse)
                            );
                            clickApplyFilterGuard.current = true;
                        }
                    }
                }
            }
        }

        // 根據 productChildField 更新 URL 中 key "79"
        if (globalFilterStateAfterApply || applyFilterResults) {
            setSearchParams((prev) => {
                if (prev.size === 0) {
                    return prev;
                }
                const newParams = new URLSearchParams(prev);
                if (!newParams.has('79')) {
                    if (productChildField) {
                        newParams.set('79', String(productChildField.id));
                        addKeyForAppliedKeys('79');
                        clickApplyFilterGuard.current = true;
                    } else {
                        newParams.delete('79');
                        removeKeyForAppliedKeys('79');
                    }
                }
                return newParams;
            });
        }
    }, [globalFilterStateAfterApply, applyFilterResults, productChildField]);

    const handleDownloadCSV = () => {
        if (basePartData && basePartData.length > 0) {
            const csv = convertToCSV(basePartData, language);
            downloadCSV(csv, 'basePartData.csv');
        }
    };

    // 這個變數是用來控制product table要使用來自filter篩選過後的basepart, 還是使用query/raw內含有的uniqueparts
    const shouldUseDetectedUniqueParts =
        storedSearchInput?.queryRawResults?.message?.type === 'WARNING' &&
        storedSearchInput.queryRawResults?.detectedUniqueParts.length > 1;

    const toggleFilterController = () => {
        setIsFilterControllerOpen((prev) => !prev);
    };

    const { isMaxScreen, isScreenXxl, isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    const useMobileProductDisplay = isScreenMd || isScreenSm || isScreenXs;

    // 這是新增的功能因為Erek想要又多一個按鈕來控制filter是否要開啟
    const [isFilterControllerOpen, setIsFilterControllerOpen] = useState<boolean>(true);

    const { compareItemPns, togglePn, clearAll } = useCompareParams();

    const [showCompareInfo, setShowCompareInfo] = useState(false);

    // 先在 component 頂層加一個 ref 來記錄「上一次真正需要做篩選」的 query fingerprint
    const prevFilterQueryRef = useRef<string>(''); // "" for first run
    const prevProductFieldIdRef = useRef<number | undefined>(undefined); // "" for first run

    // 在 useEffect 裡檢查 URL 的 query 參數
    /* ========= 🟡 解析 URL ＆ 重建 filterState ========= */
    useEffect(() => {
        console.group('%c🟡 parse-url', 'color:orange;font-weight:bold');

        /* 1. 解析目前網址 query（去掉 ?）*/
        const urlSearch = location.search.startsWith('?')
            ? location.search.slice(1) // "search=AAA+BBB&2=17..."
            : location.search; // 可能是空字串

        const urlParams = new URLSearchParams(urlSearch);

        // console.log('urlParams', urlParams.toString());

        if (urlParams.has('doCompare')) {
            // console.log('⏭ Do Compare mode, skip parsing/filter apply.');
            console.groupEnd();
            return;
        }

        /* 2. 同步到 searchParams state —— 只有內容真的不同才 set */
        if (urlParams.toString() !== searchParams.toString()) {
            // ⚠️ 用 replace:true，React-Router 只更新 state，不 push history
            setSearchParams(urlParams, { replace: true });
            // console.log('🔄  sync searchParams →', urlParams.toString());
        } else {
            // console.log('⏭  URL 和 state 相同，skip sync');
        }

        /* 3. definitions 尚未載完就先退，避免組 undefined */
        if (!productField) {
            console.log('⏭  productField 尚未 ready');
            console.groupEnd();
            return;
        }

        /* 4. 建立新的 globalFilterState / globalFilterBaseState */

        console.log('productField', productField);

        const newGlobalFilterState: DynamicRequestBody = { '1': productField.id };
        const newGlobalFilterBaseState: DynamicRequestBody = { '1': productField.id };

        urlParams.forEach((value, key) => {
            const safeKey = sanitizeKey(key);
            if (!safeKey) return;

            if (key === 'search' || key === 'compare' || key === 'doCompare') return;

            const tokens = value.split(',');
            if (tokens.length > 1) {
                newGlobalFilterState[key] = tokens.map((t) => Number(t)).filter((n) => !isNaN(n));
            } else {
                if (value.toLowerCase() === 'true') {
                    newGlobalFilterState[key] = true;
                } else if (!isNaN(Number(value))) {
                    newGlobalFilterState[key] = [Number(value)];
                }
            }
        });

        /* 5. 若網址 path 帶子分類 id，補進 79 */
        const childIdStr = getProductChildFieldFromPath(location.search);
        if (childIdStr && childIdStr.trim() !== '') {
            const childIdNum = Number(childIdStr);
            newGlobalFilterState['79'] = [childIdNum];
            newGlobalFilterBaseState['79'] = [childIdNum];
        }

        /* 6. 寫回狀態（只要內容真的變才 set，省一次 re-render）*/
        setGlobalFilterState((prev) => {
            return JSON.stringify(prev) === JSON.stringify(newGlobalFilterState)
                ? prev
                : newGlobalFilterState;
        });
        setGlobalFilterBaseState((prev) => {
            return JSON.stringify(prev) === JSON.stringify(newGlobalFilterBaseState)
                ? prev
                : newGlobalFilterBaseState;
        });
        // console.log("我在setGlobalFilterStateAfterApply(newGlobalFilterState);")
        setGlobalFilterStateAfterApply(newGlobalFilterState);

        /* 7. 只在「非 compare / doCompare 以外的參數有變動」時才跑 Apply */
        const fp = getFilterFingerprint(urlParams);

        const shouldApplyFilter =
            fp !== prevFilterQueryRef.current || productField.id !== prevProductFieldIdRef.current;
        // console.log("fp",fp)
        // console.log("prevFilterQueryRef.current",prevFilterQueryRef.current)
        if (shouldApplyFilter) {
            prevFilterQueryRef.current = fp;
            prevProductFieldIdRef.current = productField.id;
            // console.log('要 shouldApplyFilter嚕', newGlobalFilterState);
            handleApplyFilters(newGlobalFilterState); // 真的有篩選時才跑
        } else {
            console.debug('🔸 filter params unchanged → skip apply');
        }

        console.groupEnd();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [location.search, productField, storedSearchInput?.searchInput]);

    // New request https://www.wrike.com/open.htm?id=1621171359
    const pimcoreTreeDefinitionData = useMemo(() => {
        return ProductHomeProps.productGroupByDmDefinitionId[productField?.id?.toString() || ''];
    }, [productField, ProductHomeProps.productGroupByDmDefinitionId]);

    // request https://www.wrike.com/open.htm?id=1640779536
    const { setIsLoading } = useLoading();
    const { searchByKeyword } = useSearchHandler({ setIsLoading });

    useEffect(() => {
        const newSearchParams = new URLSearchParams(window.location.search);

        const searchParam = newSearchParams.get('search');

        if (newSearchParams.has('search') && !newSearchParams.has('compare')) {
            if (!ProductHomeProps.searchInput && !storedSearchInput?.searchInput && searchParam) {
                // console.log('我有進來searchByKeyword', searchParam, compareParam, doCompareParam);
                // searchByKeyword(searchParam, {
                //     compare: compareList?.length ? compareList : undefined,
                //     doCompare: doCompareFlag,
                // });
                searchByKeyword(searchParam);
            }
        }
    }, [storedSearchInput]);

    // 1) 只取出 search 參數，其他參數變動不會影響
    const searchParam = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const v = params.get('search');
        return v ? v.trim() : null;
    }, [location.search]);

    const { isInitialSearchLoading } = useInitialSearchLoading(
        searchParam,
        globalFilterStateAfterApply,
        applyFilterResults
    );

    // 定義各個 Tab 項目的內容與條件
    const tabItems = [
        {
            name: 'Products',
            label: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <div className="iconHover" style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 18, fontFamily: 'Poppins', marginLeft: 2 }}>
                            {ProductHomeProps.contentTree.common.tabs.Products.title.content}
                        </Typography>
                    </div>
                </Box>
            ),
            panel: (
                <VerticalContainer containerName={'Products'}>
                    {/* Products Tab 的內容 */}
                    {productField && (
                        <SubLevelInfo
                            setGlobalFilterState={setGlobalFilterState}
                            singleSelectKey={singleSelectKey}
                            multipleSelectKeys={multipleSelectKeys}
                            productField={productField}
                            definitionsMap={definitionsMap}
                            language={language}
                            onlyHandleFilterChange={onlyHandleFilterChange}
                            setGlobalFilterStateAfterApply={setGlobalFilterStateAfterApply}
                            productChildField={productChildField}
                            queryRawResults={storedSearchInput?.queryRawResults}
                            isFilterControllerOpen={isFilterControllerOpen}
                            setProductChildField={setProductChildField}
                            contentTree={ProductHomeProps.contentTree}
                            setAppliedKeys={setAppliedKeys}
                        />
                    )}

                    {!useMobileProductDisplay && (
                        <CompareContainer
                            compareItemPns={compareItemPns}
                            togglePn={togglePn}
                            clearAll={clearAll}
                            showCompareInfo={showCompareInfo}
                            setShowCompareInfo={setShowCompareInfo}
                            contentTree={ProductHomeProps.contentTree}
                        />
                    )}

                    {useMobileProductDisplay ? (
                        <MobileFilterSection
                            isFilterControllerOpen={isFilterControllerOpen}
                            toggleFilterController={toggleFilterController}
                            partCounts={
                                shouldUseDetectedUniqueParts
                                    ? storedSearchInput?.queryRawResults?.detectedUniqueParts
                                          .length || 0
                                    : getPartCount(productField, productChildField, partCounts) || 0
                            }
                            handleApplyFilters={() => handleApplyFilters()}
                            isApplyFiltersEnabled={isApplyFiltersEnabled}
                            contentTree={ProductHomeProps.contentTree}
                        >
                            {productField && (
                                <SubLevelInfo
                                    setGlobalFilterState={setGlobalFilterState}
                                    singleSelectKey={singleSelectKey}
                                    multipleSelectKeys={multipleSelectKeys}
                                    productField={productField}
                                    definitionsMap={definitionsMap}
                                    language={language}
                                    onlyHandleFilterChange={onlyHandleFilterChange}
                                    setGlobalFilterStateAfterApply={setGlobalFilterStateAfterApply}
                                    productChildField={productChildField}
                                    queryRawResults={storedSearchInput?.queryRawResults}
                                    isFilterControllerOpen={isFilterControllerOpen}
                                    setProductChildField={setProductChildField}
                                    contentTree={ProductHomeProps.contentTree}
                                    setAppliedKeys={setAppliedKeys}
                                />
                            )}
                            <ProductFilterContainer isFilterControllerOpen={isFilterControllerOpen}>
                                {isFilterControllerOpen &&
                                    stableOriginData &&
                                    !originDataLoading && (
                                        <FilterCheckBox
                                            setGlobalFilterState={setGlobalFilterState}
                                            globalFilterState={globalFilterState}
                                            originMFG={
                                                filterDynamicResponseForMFG(stableOriginData) ||
                                                emptyMFGParameterFilter()
                                            }
                                            afterFilterMFG={
                                                applyFilterResults
                                                    ? filterDynamicResponseForMFG(
                                                          applyFilterResults
                                                      )
                                                    : undefined
                                            }
                                            originCheckBox={
                                                filterDynamicResponseForCheckBox(
                                                    stableOriginData
                                                ) ?? []
                                            }
                                            afterFilterCheckBox={
                                                applyFilterResults
                                                    ? filterDynamicResponseForCheckBox(
                                                          applyFilterResults
                                                      )
                                                    : undefined
                                            }
                                            language={language}
                                            globalFilterStateAfterApply={
                                                globalFilterStateAfterApply
                                            }
                                        />
                                    )}
                                {stableOriginData && !originDataLoading && productField && (
                                    <FilterSection
                                        originData={
                                            filterDynamicResponseForFilters(stableOriginData) ?? []
                                        }
                                        setGlobalFilterState={setGlobalFilterState}
                                        globalFilterState={globalFilterState}
                                        firstKeyAllowedMultipleSelect={
                                            firstKeyAllowedMultipleSelect
                                        }
                                        productField={productField}
                                        appliedKeysSet={appliedKeys}
                                        applyFilterResults={
                                            applyFilterResults
                                                ? filterDynamicResponseForFilters(
                                                      applyFilterResults
                                                  )
                                                : undefined
                                        }
                                        language={language}
                                        tempFilterResults={
                                            filterResults
                                                ? filterDynamicResponseForFilters(filterResults)
                                                : undefined
                                        }
                                        isFilterControllerOpen={isFilterControllerOpen}
                                        toggleFilterController={toggleFilterController}
                                        resetFilterSignal={resetFilterSignal}
                                        contentTree={ProductHomeProps.contentTree}
                                        isLoading={isInitialSearchLoading}
                                    />
                                )}
                            </ProductFilterContainer>
                        </MobileFilterSection>
                    ) : (
                        <>
                            <ProductFilterContainer isFilterControllerOpen={isFilterControllerOpen}>
                                {/* 相同內容，不用 MobileFilterSection 包裹 */}
                                {stableOriginData && !originDataLoading && productField && (
                                    <FilterSection
                                        originData={
                                            filterDynamicResponseForFilters(stableOriginData) ?? []
                                        }
                                        setGlobalFilterState={setGlobalFilterState}
                                        globalFilterState={globalFilterState}
                                        firstKeyAllowedMultipleSelect={
                                            firstKeyAllowedMultipleSelect
                                        }
                                        productField={productField}
                                        appliedKeysSet={appliedKeys}
                                        applyFilterResults={
                                            applyFilterResults
                                                ? filterDynamicResponseForFilters(
                                                      applyFilterResults
                                                  )
                                                : undefined
                                        }
                                        language={language}
                                        tempFilterResults={
                                            filterResults
                                                ? filterDynamicResponseForFilters(filterResults)
                                                : undefined
                                        }
                                        isFilterControllerOpen={isFilterControllerOpen}
                                        toggleFilterController={toggleFilterController}
                                        resetFilterSignal={resetFilterSignal}
                                        contentTree={ProductHomeProps.contentTree}
                                        isLoading={isInitialSearchLoading}
                                    />
                                )}
                                {isFilterControllerOpen &&
                                    stableOriginData &&
                                    !originDataLoading && (
                                        <FilterCheckBox
                                            setGlobalFilterState={setGlobalFilterState}
                                            globalFilterState={globalFilterState}
                                            originMFG={
                                                filterDynamicResponseForMFG(stableOriginData) ||
                                                emptyMFGParameterFilter()
                                            }
                                            afterFilterMFG={
                                                applyFilterResults
                                                    ? filterDynamicResponseForMFG(
                                                          applyFilterResults
                                                      )
                                                    : undefined
                                            }
                                            originCheckBox={
                                                filterDynamicResponseForCheckBox(
                                                    stableOriginData
                                                ) ?? []
                                            }
                                            afterFilterCheckBox={
                                                applyFilterResults
                                                    ? filterDynamicResponseForCheckBox(
                                                          applyFilterResults
                                                      )
                                                    : undefined
                                            }
                                            language={language}
                                            globalFilterStateAfterApply={
                                                globalFilterStateAfterApply
                                            }
                                        />
                                    )}
                                {isFilterControllerOpen && stableOriginData && productField && (
                                    <FilterButtonSection
                                        partCount={
                                            shouldUseDetectedUniqueParts
                                                ? storedSearchInput?.queryRawResults
                                                      ?.detectedUniqueParts.length || 0
                                                : getPartCount(
                                                      productField,
                                                      productChildField,
                                                      partCounts
                                                  ) || 0
                                        }
                                        setGlobalFilterState={setGlobalFilterState}
                                        setFirstKeyAllowedMultipleSelect={
                                            setFirstKeyAllowedMultipleSelect
                                        }
                                        isApplyFiltersEnabled={isApplyFiltersEnabled}
                                        basePartTrigger={basePartTrigger}
                                        globalFilterState={globalFilterState}
                                        handleDownloadCSV={handleDownloadCSV}
                                        setAppliedKeys={setAppliedKeys}
                                        setApplyFilterResults={setApplyFilterResults}
                                        handleApplyFilters={() => handleApplyFilters()}
                                        setFilterResults={setFilterResults}
                                        setProductChildField={setProductChildField}
                                        setGlobalFilterStateAfterApply={
                                            setGlobalFilterStateAfterApply
                                        }
                                        setSearchParams={setSearchParams}
                                        productField={productField}
                                        setPartCounts={setPartCounts}
                                        setResetFilterSignal={setResetFilterSignal}
                                        setStoredSearchInput={() => setStoredSearchInput(undefined)}
                                        setFilterKeyOrder={() => setFilterKeyOrder([])}
                                        setSortedParts={setSortedParts}
                                        contentTree={ProductHomeProps.contentTree}
                                    />
                                )}
                            </ProductFilterContainer>
                        </>
                    )}

                    {/* Products Tab 最後的表格 */}
                    {!useMobileProductDisplay && definitionsMap && productField && (
                        <ProductTable
                            basePartResults={sortedParts ?? []}
                            partCount={applyFilterResults?.counts || 0}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            treeName={productField?.displayName}
                            ascColumn={ascColumn}
                            setAscColumn={setAscColumn}
                            descColumn={descColumn}
                            setDescColumn={setDescColumn}
                            page={page}
                            setPage={setPage}
                            isTableLoading={isBasePartTableLoading}
                            setIsNumericSort={setIsNumericSort}
                            detectedUniqueParts={
                                shouldUseDetectedUniqueParts
                                    ? storedSearchInput.queryRawResults?.detectedUniqueParts
                                    : undefined
                            }
                            setIsBasePartTableLoading={setIsBasePartTableLoading}
                            levelId={extractTypeParameterIds(applyFilterResults)}
                            definitionsMap={definitionsMap}
                            compareItemPns={compareItemPns}
                            onToggleCompareItem={togglePn}
                            contentTree={ProductHomeProps.contentTree}
                        />
                    )}
                    {!isFilterControllerOpen && useMobileProductDisplay && (
                        <MobileProductDisplay
                            basePartResults={sortedParts ?? []}
                            partCount={
                                shouldUseDetectedUniqueParts
                                    ? storedSearchInput?.queryRawResults?.detectedUniqueParts
                                          .length || 0
                                    : getPartCount(productField, productChildField, partCounts) || 0
                            }
                            detectedUniqueParts={
                                shouldUseDetectedUniqueParts
                                    ? storedSearchInput.queryRawResults?.detectedUniqueParts
                                    : undefined
                            }
                            page={page}
                            setPage={setPage}
                            rowsPerPage={rowsPerPage}
                            contentTree={ProductHomeProps.contentTree}
                        />
                    )}
                </VerticalContainer>
            ),
        },
        {
            name: 'Datasheets',
            label: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <div className="iconHover" style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 18, fontFamily: 'Poppins', marginLeft: 2 }}>
                            {ProductHomeProps.contentTree.common.tabs.Datasheets.title.content}
                        </Typography>
                    </div>
                </Box>
            ),
            panel: (
                <PrefilteredResourceLibraryItemsProvider
                    initialFilters={{
                        resourceTypeKeys: ['Datasheet'],
                        productGroupKeys: [
                            productChildField
                                ? `${productField?.displayName ?? ''}/${productChildField.displayName ?? ''}`
                                : productField?.displayName ?? '',
                        ],
                        resourceAssetPaths: filterResults?.datasheets,
                    }}
                    itemsPerPage={10}
                    sortOrder="latest"
                >
                    <PreFilteredResourcesList backgroundColor={''} isTechnicalResources={false} />
                </PrefilteredResourceLibraryItemsProvider>
            ),
        },
        {
            name: 'Resources',
            label: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <div className="iconHover" style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 18, fontFamily: 'Poppins', marginLeft: 2 }}>
                            {ProductHomeProps.contentTree.common.tabs.Resources.title.content}
                        </Typography>
                    </div>
                </Box>
            ),
            panel: (
                <PrefilteredResourceLibraryItemsProvider
                    initialFilters={{
                        resourceTypeKeys: resourceTypeKeysWithoutDataSheet,
                        ...(!storedSearchInput?.searchInput
                            ? {
                                  productGroupKeys: [
                                      productChildField
                                          ? `${productField?.displayName ?? ''}/${productChildField.displayName ?? ''}`
                                          : productField?.displayName ?? '',
                                  ],
                              }
                            : {}),
                        searchTerm: storedSearchInput?.searchInput,
                    }}
                    itemsPerPage={10}
                    sortOrder="latest"
                >
                    <PreFilteredResourcesList backgroundColor={''} isTechnicalResources={false} />
                </PrefilteredResourceLibraryItemsProvider>
            ),
        },
        {
            name: 'Simulations Tools',
            label: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <div className="iconHover" style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 18, fontFamily: 'Poppins', marginLeft: 2 }}>
                            {ProductHomeProps.contentTree.common.tabs.SimulationTools.title.content}
                        </Typography>
                    </div>
                </Box>
            ),
            panel: <ToolPage cmsContent={toolsContent.Overview} />,
        },
    ];
    return (
        <IntlProvider locale={locale} key={locale} defaultLocale="en" messages={messages}>
            {(isMaxScreen || isScreenXxl) && (
                <div key={location.pathname} style={styles.productPageContainer}>
                    <LargeScreenBreadcrumbsContainer>
                        <ProductsBreadCrumbs
                            breadCrumbs={[
                                {
                                    name: getDisplayNameWithFallback(
                                        productField?.displayNames,
                                        language,
                                        productField?.displayName ?? ''
                                    ),
                                    url: getProductUrl(productField),
                                },
                                {
                                    name: getDisplayNameWithFallback(
                                        productChildField?.displayNames,
                                        language,
                                        productChildField?.displayName ?? ''
                                    ),
                                    url: getProductUrl(productField, productChildField),
                                },
                            ].filter((name) => name.name)}
                        />
                    </LargeScreenBreadcrumbsContainer>
                    <ProductsBanner
                        backgroundImageUrl={
                            pimcoreTreeDefinitionData?.backgroundImageUrl ?? undefined
                        }
                    >
                        <h1 style={styles.bannerFont} data-testid="tree-banner-text">
                            {storedSearchInput?.searchInput
                                ? `${ProductHomeProps.contentTree.common.searchResultsPageTitle.content} – ${storedSearchInput?.searchInput}`
                                : getDisplayNameWithFallback(
                                      productField?.displayNames,
                                      language,
                                      productField?.displayName ?? ''
                                  )}
                        </h1>
                        {/* 呼叫 getBannerText，若文字中包含 *...* 就會自動轉成 <strong> */}
                        <span style={styles.bannerFont2} data-testid="banner-description">
                            {storedSearchInput?.customBannerMessage
                                ? storedSearchInput?.customBannerMessage
                                : getBannerText(
                                      storedSearchInput,
                                      pimcoreTreeDefinitionData,
                                      productChildField,
                                      language
                                  )}
                        </span>
                    </ProductsBanner>
                    <Box sx={{ width: '100%' }} data-testid={'outter_box'}>
                        <Box sx={{ backgroundColor: '#7A7C81' }}>
                            <Tabs
                                sx={{ marginLeft: '3rem' }}
                                TabIndicatorProps={{ hidden: true }}
                                value={value}
                                onChange={handleChange}
                                aria-label="product tabs"
                            >
                                {tabItems.map((tab, index) => (
                                    <Tab
                                        key={index}
                                        label={tab.label}
                                        {...a11yProps(index)}
                                        sx={styles.productTabsStyle('white')}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                        {tabItems.map((tab, index) => (
                            <CustomTabPanel key={index} value={value} index={index}>
                                {tab.panel}
                            </CustomTabPanel>
                        ))}
                    </Box>
                </div>
            )}
            {!(isMaxScreen || isScreenXxl) && productField && (
                <ReusableTabs
                    bannerImage={
                        pimcoreTreeDefinitionData?.backgroundImageUrl
                            ? `${PIMCORE_CONTENT_BASE_URL}${pimcoreTreeDefinitionData.backgroundImageUrl}`
                            : ''
                    }
                    bannerTitle={
                        storedSearchInput?.searchInput
                            ? `${ProductHomeProps.contentTree.common.searchResultsPageTitle.content} – ${storedSearchInput?.searchInput}`
                            : getDisplayNameWithFallback(
                                  productField?.displayNames,
                                  language,
                                  productField?.displayName ?? ''
                              )
                    }
                    bannerDescription={
                        storedSearchInput?.customBannerMessage
                            ? storedSearchInput?.customBannerMessage
                            : getBannerText(
                                  storedSearchInput,
                                  pimcoreTreeDefinitionData,
                                  productChildField,
                                  language
                              )
                    }
                    tabLabels={[
                        ProductHomeProps.contentTree.common.tabs.Products.title.content,
                        ProductHomeProps.contentTree.common.tabs.Datasheets.title.content,
                        ProductHomeProps.contentTree.common.tabs.Resources.title.content,
                        ProductHomeProps.contentTree.common.tabs.SimulationTools.title.content,
                    ]}
                    tabKeys={tabItems.map((item) => item.name)}
                    tabContents={tabItems.map((item) => item.panel)}
                    bannerFontStyle={styles.bannerFont}
                    subBannerFontStyle={styles.bannerFont2}
                    pageName={'Products'}
                    showTabs={true}
                    breadcrumbs={
                        <ProductsBreadCrumbs
                            breadCrumbs={[
                                {
                                    name: getDisplayNameWithFallback(
                                        productField?.displayNames,
                                        language,
                                        productField?.displayName ?? ''
                                    ),
                                    url: getProductUrl(productField),
                                },
                                {
                                    name: getDisplayNameWithFallback(
                                        productChildField?.displayNames,
                                        language,
                                        productChildField?.displayName ?? ''
                                    ),
                                    url: getProductUrl(productField, productChildField),
                                },
                            ].filter((name) => name.name)}
                        />
                    }
                    changeUrl={false}
                />
            )}
        </IntlProvider>
    );
};

export default ProductHome;
